import { useCallback, useState } from "react";
import DSButton from "../../../system-design/DSButton";
import DSCircularProgress from "../../../system-design/DSCircularProgress";
import DSDialog from "../../../system-design/DSDialog";
import DSDialogContent from "../../../system-design/DSDialogContent";
import DSDialogActions from "../../../system-design/DSDialogActions";
import DSErrorButton from "../../../system-design/DSErrorButton";
import DSMenuItem from "../../../system-design/DSMenuItem";
import DSStack from "../../../system-design/DSStack";
import DSTextField from "../../../system-design/DSTextField";
import DSTypography from "../../../system-design/DSTypography";
import useTransferDeviceDataMutation from "../../hooks/useTransferDeviceDataMutation";
import { TransferDeviceDataSummary } from "../../../../api/admin";
import firebaseService from "../../../../firebase_service/firebaseService";

// The target field takes a clock id, idf or serial; the API needs the clock id.
// A clock with no machine doc yet can't be found by idf, so the input is then
// sent as-is and the API decides whether it is a real clock id.
const resolveClockId = async (input: string) => {
  try {
    return (await firebaseService.getDeviceBySerialOrIdf(input)).id;
  } catch {
    return input;
  }
};

// Every option starts unanswered, so the admin must pick Yes or No for each.
type Choice = "" | "yes" | "no";

const YesNoField = ({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: Choice;
  onChange: (value: Choice) => void;
  disabled: boolean;
}) => (
  <DSTextField
    select
    size="small"
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value as Choice)}
    disabled={disabled}
    fullWidth
  >
    <DSMenuItem value="yes">Yes</DSMenuItem>
    <DSMenuItem value="no">No</DSMenuItem>
  </DSTextField>
);

type TransferDeviceDialogProps = {
  open: boolean;
  onClose: () => void;
  sourceClockId: string;
  sourceIdf: string;
};

const TransferDeviceDialog = ({
  open,
  onClose,
  sourceClockId,
  sourceIdf,
}: TransferDeviceDialogProps) => {
  const { mutateAsync, isPending } =
    useTransferDeviceDataMutation(sourceClockId);
  const [targetClockId, setTargetClockId] = useState("");
  const [warning, setWarning] = useState("");
  const [transferDataChoice, setTransferDataChoice] = useState<Choice>("");
  const [keepSourceDataChoice, setKeepSourceDataChoice] = useState<Choice>("");
  const [transferCollectionsChoice, setTransferCollectionsChoice] =
    useState<Choice>("");
  const transferData = transferDataChoice === "yes";
  const keepSourceData = transferData && keepSourceDataChoice === "yes";
  const transferCollections = transferCollectionsChoice === "yes";
  // Keep original clock data is only asked when data is transferred.
  const allAnswered =
    transferDataChoice !== "" &&
    transferCollectionsChoice !== "" &&
    (!transferData || keepSourceDataChoice !== "");
  // Set when the target already has data — the user must confirm the overwrite.
  const [pendingOverwriteIdf, setPendingOverwriteIdf] = useState<string | null>(
    null,
  );
  const [summary, setSummary] = useState<TransferDeviceDataSummary | null>(
    null,
  );

  const resetState = useCallback(() => {
    setTargetClockId("");
    setWarning("");
    setPendingOverwriteIdf(null);
    setSummary(null);
    setTransferDataChoice("");
    setKeepSourceDataChoice("");
    setTransferCollectionsChoice("");
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const runTransfer = useCallback(
    async (overwrite: boolean) => {
      const trimmed = targetClockId.trim();
      if (!trimmed) {
        setWarning("Target clock is required");
        return;
      }
      setWarning("");
      try {
        const resolvedTargetId = await resolveClockId(trimmed);
        if (resolvedTargetId === sourceClockId) {
          setWarning("Target clock must be different from the source");
          return;
        }
        const result = await mutateAsync({
          targetClockId: resolvedTargetId,
          overwrite,
          transferData,
          keepSourceData,
          transferCollections,
        });
        if (result.status === "confirmation_required") {
          setPendingOverwriteIdf(result.targetIdf ?? trimmed);
          return;
        }
        setSummary(result.summary);
      } catch (error) {
        setWarning((error as Error).message || "Something went wrong");
      }
    },
    [
      targetClockId,
      sourceClockId,
      transferData,
      keepSourceData,
      transferCollections,
      mutateAsync,
    ],
  );

  const copiedData = keepSourceData;
  const resultLines: string[] = [];
  if (transferData) {
    resultLines.push(
      copiedData
        ? "Data copied. The source kept its data."
        : "Data moved and the source was reset.",
    );
  }
  resultLines.push(
    transferCollections
      ? "Collections moved."
      : "Collections stayed on the source.",
  );

  return (
    <DSDialog open={open} onClose={handleClose}>
      <DSDialogContent>
        {summary ? (
          <DSStack spacing={1}>
            <DSTypography variant="h6">Transfer complete</DSTypography>
            <DSTypography variant="body2">
              <b>{sourceIdf}</b> → <b>{targetClockId.trim()}</b>.{" "}
              {resultLines.join(" ")}
            </DSTypography>
            <DSTypography variant="body2">
              Machine document: {summary.machineDoc ? "✓" : "—"}
            </DSTypography>
            <DSTypography variant="body2">
              Machine report: {summary.machineReport ? "✓" : "—"}
            </DSTypography>
            <DSTypography variant="body2">
              Gifts {copiedData ? "shared" : "updated"}: {summary.giftsUpdated}
            </DSTypography>
            <DSTypography variant="body2">
              Collection codes updated: {summary.collectionCodesUpdated}
              {summary.collectionCodeRedemptionsUpdated > 0
                ? ` (+${summary.collectionCodeRedemptionsUpdated} redemptions)`
                : ""}
            </DSTypography>
          </DSStack>
        ) : (
          <DSStack spacing={1.5}>
            <DSTypography variant="h6">Transfer device data</DSTypography>
            <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
              Moves what you choose from <b>{sourceIdf}</b> to the target clock
              and removes it from this device. Device fields (id, idf, version,
              firmware) stay with each physical clock. The source is only
              changed after everything is validated.
            </DSTypography>
            <DSTextField
              label="Target clock (id, idf or serial)"
              value={targetClockId}
              onChange={(e) => {
                setTargetClockId(e.target.value);
                setPendingOverwriteIdf(null);
              }}
              fullWidth
              autoFocus
            />
            <YesNoField
              label="Transfer data (except collections)"
              value={transferDataChoice}
              onChange={(value) => {
                setTransferDataChoice(value);
                setPendingOverwriteIdf(null);
              }}
              disabled={isPending}
            />
            {transferData && (
              <DSStack sx={{ pl: 4 }}>
                <YesNoField
                  label="Keep original clock data"
                  value={keepSourceDataChoice}
                  onChange={setKeepSourceDataChoice}
                  disabled={isPending}
                />
              </DSStack>
            )}
            <YesNoField
              label="Transfer collections (with collection codes)"
              value={transferCollectionsChoice}
              onChange={setTransferCollectionsChoice}
              disabled={isPending}
            />
            {pendingOverwriteIdf && (
              <DSTypography color="warning.main" variant="body2">
                Target clock {pendingOverwriteIdf} already has cloud data.
                Overwriting will replace its data and keep its collections.
                Press “Overwrite & transfer” to continue.
              </DSTypography>
            )}
            {warning && (
              <DSTypography color="error" variant="body2">
                {warning}
              </DSTypography>
            )}
          </DSStack>
        )}
      </DSDialogContent>
      <DSDialogActions>
        {summary ? (
          <DSButton onClick={handleClose}>Done</DSButton>
        ) : (
          <>
            <DSButton onClick={handleClose} disabled={isPending}>
              Cancel
            </DSButton>
            <DSErrorButton
              disabled={
                isPending ||
                !targetClockId.trim() ||
                !allAnswered ||
                (!transferData && !transferCollections)
              }
              onClick={() => runTransfer(!!pendingOverwriteIdf)}
            >
              {isPending && <DSCircularProgress size={16} sx={{ mr: 1 }} />}
              {pendingOverwriteIdf ? "Overwrite & transfer" : "Transfer"}
            </DSErrorButton>
          </>
        )}
      </DSDialogActions>
    </DSDialog>
  );
};

export default TransferDeviceDialog;
